import { RouterLinkTarget } from './router-link-target';
import {
  addUrlChangeListener,
  resolve,
  navigateTo,
  hasSharedBase,
  removeUrlChangeListener,
  URLChangeListenerFunction,
  URLInitOptions,
  URLGlobalOptions,
} from '../../core/routing';

import { SW_ROUTER_LINK_AVAILABLE_ATTRIBUTES, SW_TARGET_UPDATE_EVENT } from './router-link.config';
import { defineElement } from '../../core/routing.util';

/**
 * Function type for the navigation hook that is called after clicking on the anchor
 * tag, but before the routing navigation is triggered.
 * `() => void | Promise<void>`
 */
export type BeforeNavigationFunction = () => void | Promise<void>;

/**
 * `RouterLink`: a NativeJS Web Component for creating self-updating links which interface with the
 * main routing library functions.
 *
 * ---------
 *
 * **URL resolution**
 *
 * `RouterLink`s are an abstraction layer between you and the routing library's `resolve` function.
 * You describe the URL you want to link to via the component's attributes, and URL resolution and update
 * are handled for you. Supported attributes are:
 * - `namespace` - required, every link option must be namespaced to some string
 * - `pathname` - the pathname you want to link out to
 * - `searchParams` - the search parameters you want to link out to
 * - `hash` - the hash string you want to link out to
 *
 * If you want to have more than one option, you should add `RouterLinkTarget` components inside
 * the RouterLink component, and use the same attributes stated above. In order for the `RouterLink`
 * component read these targets correctly, you should add the `slot="target"` attribute to the component.
 *
 * IMPORTANT: Bear in mind that `RouterLink` will check for `RouterLinkTarget` components before checking it's
 * own options. If `RouterLinkTarget` components exist, it will not compute `RouterLink` attributes
 * and that namespace will not be resolved.
 *
 * By using `RouterLink`, all your links will self-update every time page changes. To understand
 * why this is necessary, think, for example, of a page where two web components which want to modify
 * the URL exist: yours, component X, and some other one, component Y.
 *
 * Now, you want to have links for two different pages within your component, a `/main` page and an
 * `/about` page. At the same time, component Y wants to show some search results and have `/page/1`,
 * `/page/2` and so on.
 *
 * The main routing library functions always resolve URLs against the current URL, so if you're on a page
 * like `xPath=/main&yPath=/page/1` and resolve for `{ namespace: 'x', pathname: '/about' }`, you'll get an
 * URL pointing to `xPath=/about&yPath=/page/1`.
 *
 * This is fine, but now assume the user interacts with component Y and changes the page to page 2.
 * Now the page is `xPath=/about&yPath=/page/2`, and if the user clicks on the URL you resolved before
 * you'll be changing component Y's state! To avoid overwritting other component's state you need to
 * re-resolve your URL every time the page's URL changes.
 *
 * If you use `RouterLink`, this is given to you for free. You just need to describe where you want
 * your link to point. For the example above it would be:
 *
 *     <sw-router-link namespace="x" pathname="/about">
 *       About
 *     </sw-router-link>
 *
 *  or, if you want more than one option:
 *
 *     <sw-router-link>
 *       <sw-router-link-target slot="target" namespace="car-preview" path="classA"></sw-router-link-target>
 *       <sw-router-link-target slot="target" namespace="financial-calculator" path="classA"></sw-router-link-target>
 *       Check more
 *     </sw-router-link>
 *
 * At first render the internal `<a>` would resolve to `xPath=/about&yPath=/page/1`, just like a manual
 * call to the `resolve` function would. But after the user interacts with the second component, all the
 * other `<sw-router-links>` automatically re-resolve and update themselves, this one in particular would
 * then point to `xPath=/about&yPath=/page/2`, without you, as the component user, needing to do anything.
 *
 * ---------
 *
 * **Custom Content & Customization**
 *
 * This Web Component has one slot inside the internal `<a>` tag. Any content that is put between this
 * component's tags will be shown inside the internal `<a>` tag. If you need your links to have custom
 * content, you can use this slot to write any HTML that is otherwise allowed inside anchor tags.
 *
 * For stylization, a `::part(a)` hook is provided. For example, if you you need to provide a link that
 * looks like a button, with an icon, you can write the following HTML and CSS
 *
 *     <sw-router-link class="as-button" ...attrs>
 *       <img src="/assets/next.png" alt=""/>
 *       <span>Next Page</span>
 *
 * ---------
 *
 * **Events and Code Execution**
 *
 * Just like with any other anchor tag, the click event will propagate from inside this component and out.
 * So if you need to do something after the user clicks the anchor, you can listen to the usual event
 *
 *     document.querySelector('sw-router-link').addEventListener('click', (clickEvent) => {
 *       // user clicked the link, do whatever
 *     });
 *
 * However, by this time the navigation has already ocurred, because the component itself reacts to the
 * click event immediately. In case you need to do something *before* navigation occurs you can use the
 * `beforeNavigation` property, accessible via JavaScript, by doing:
 *
 *     document.querySelector('sw-router-link').beforeNavigation = () => {
 *       // navigation is about to occur, do something
 *     });
 *
 * In particular, if a `Promise` object is returned, it will be `await`ed on before the actual navigation
 * is triggered in the routing library's functions. Use this carefully, and only for very short tasks, since
 * long tasks will be noticed by users and serverly affect the site's responsiveness metrics.
 *
 * @export
 * @class RouterLink
 * @extends {HTMLElement}
 */
export class RouterLink extends HTMLElement {
  /**
   * The dynamic attributes supported by the `RouterLink` web component.
   * - `namespace` - required, without it the link will never resolve, and wil behave as normal text
   * - `pathname` - the pathname of the target destination (e.g. `"/foo"`)
   * - `search-params` - the search parameters of the target destination (e.g. `"foo=bar"`)
   * - `hash` - the hash of the target destination (e.g. `"baz"`)
   *
   * @readonly
   * @static
   * @type {string[]}
   * @memberof RouterLink
   */
  static get observedAttributes(): string[] {
    return SW_ROUTER_LINK_AVAILABLE_ATTRIBUTES;
  }

  /**
   * The internal anchor element that points to the resolved URL.
   * @ignore
   * @private
   * @type {HTMLAnchorElement}
   * @memberof RouterLink
   */
  private anchorElement: HTMLAnchorElement;

  /**
   * The internal link element that points to the anchor element.
   * @ignore
   * @private
   * @type {HTMLLinkElement}
   * @memberof RouterLink
   */
  private linkElement: HTMLLinkElement | null = null;

  /**
   * The slot for `<sw-router-link-target>` elements
   * @ignore
   * @private
   * @type {HTMLSlotElement}
   * @memberof RouterLink
   */
  private linkTargetSlotElement: HTMLSlotElement;

  /**
   * The unique identifier (key) for RouterLink component
   * @ignore
   * @public
   * @readonly
   * @type {symbol}
   * @memberof RouterLinkTarget
   */
  private internalTargetKey: symbol;

  /**
   * The resolved URL, the result of the routing library's `resolve` call with the component's attributes.
   * @ignore
   * @private
   * @type {(URL | null)}
   * @memberof RouterLink
   */
  private targetUrl: URL | null = null;

  /**
   * Boolean value that tells if there are `<ws-router-link-target>` elements inside RouterLink
   */
  private hasTargetChildren = false;

  /**
   * The handler function that re-resolves the `targetURL` every time a `URLChangeEvent` is triggered
   * by the routing library.
   * @ignore
   * @private
   * @type {URLChangeListenerFunction}
   * @memberof RouterLink
   */
  private onUrlChange: URLChangeListenerFunction;

  /**
   * The function provided to the `beforeNavigation` hook from the outside world.
   * @ignore
   * @private
   * @type {(BeforeNavigationFunction | null)}
   * @memberof RouterLink
   */
  private beforeHook: BeforeNavigationFunction | null = null;

  /**
   * The in-memory holder of unique URLInitOptions for each `<sw-router-target-link>` and `<sw-router-link>` elements
   * @ignore
   * @private
   * @type {Map<symbol, URLInitOptions>}
   * @memberof RouterLink
   */
  private elementOptions: Map<symbol, URLInitOptions>;

  /**
   * The in-memory holder of URLGlobalOptions for each `<sw-router-link>` element
   * @ignore
   * @private
   * @type {URLGlobalOptions}
   * @memberof RouterLink
   */
  private elementGlobalOptions: URLGlobalOptions;

  /** @ignore */
  constructor() {
    super();
    this.internalTargetKey = Symbol();
    this.elementOptions = new Map([[this.internalTargetKey, { namespace: '' }]]);
    this.onUrlChange = () => this.updateHref();
    this.elementGlobalOptions = { clear: false, base: null };

    const shadowRoot = this.attachShadow({ mode: 'closed' });

    // Anchor slot element
    this.anchorElement = document.createElement('a');
    this.anchorElement.setAttribute('part', 'a');
    this.anchorElement.addEventListener('click', this.onClickHandler.bind(this));

    // Slot element
    const slotElement = document.createElement('slot');
    this.anchorElement.append(slotElement);
    shadowRoot.append(this.anchorElement);

    // LinkTarget slot element
    this.linkTargetSlotElement = document.createElement('slot');
    this.linkTargetSlotElement.setAttribute('name', 'target');
    shadowRoot.append(this.linkTargetSlotElement);

    this.addEventListener(SW_TARGET_UPDATE_EVENT, this.refreshUrlFromElementOptions as EventListener);
    this.linkTargetSlotElement.addEventListener('slotchange', () => this.refreshUrlFromTargets());
  }

  /**
   * function that is called when init or the href attribute is updated if the prefetch attribute is set.
   * It sets on hover events to create the prefetch link
   */
  private setOnHoverPrefetch(): void {
    this.anchorElement.addEventListener('mouseover', () => this.createPrefetchLink());
    this.anchorElement.addEventListener('touchstart', () => this.createPrefetchLink());
  }

  /**
   * When anchor on hover event is dispatched
   * creates a prefetch link element in the head of the document, based in the anchor element
   */
  private createPrefetchLink(): void {
    if (!this.linkElement) {
      this.linkElement = document.createElement('link');
      this.linkElement.rel = 'prefetch';
      this.linkElement.href = this.anchorElement.getAttribute('href') || '';
      this.linkElement.fetchPriority = 'high';
      this.linkElement.as = 'document';
      document.head.appendChild(this.linkElement);
    }
  }
  /**
   * Updates the UrlParts for multiple namespaces when a `slotchange` event occurs.
   *
   * It will go through the RouterLink slot element and check if the RouterLinkTarget
   * elements inside it match with the elementOptions object. If new elements are added
   * or removed, it updates the Map accordingly.
   *
   * After this, it will call the updateHref method.
   *
   * @ignore
   * @memberof
    RouterLink
   */
  private refreshUrlFromTargets(): void {
    const slotElements = this.linkTargetSlotElement.assignedElements();
    const targetElements = slotElements.filter((element) => element instanceof RouterLinkTarget) as RouterLinkTarget[];
    const optionTargetKeys = targetElements.map((optionTargetElement) => optionTargetElement.targetKey);

    if (targetElements.length > 0) {
      const validOptionEntries: [symbol, URLInitOptions][] = [];

      this.hasTargetChildren = true;

      // Checks for removed target elements
      for (const [key, value] of this.elementOptions.entries()) {
        if (optionTargetKeys.includes(key)) {
          validOptionEntries.push([key, value]);
        }
      }

      // Checks for inserted target elements
      targetElements.forEach((targetElement) => {
        const isInOptionsArray = validOptionEntries.some((option) => option[0] === targetElement.targetKey);

        // if not in options array, this targetElement is added
        if (!isInOptionsArray) {
          if (this.hasValidNamespace(targetElement.urlParts.namespace)) {
            validOptionEntries.push([targetElement.targetKey, targetElement.urlParts]);
          }
        }
      });

      // Keep internal options object (if exists)
      const internalOptions = this.elementOptions.get(this.internalTargetKey);
      if (internalOptions) {
        validOptionEntries.push([this.internalTargetKey, internalOptions]);
      }
      this.elementOptions = new Map(validOptionEntries);
    } else {
      this.hasTargetChildren = false;
    }

    // Finally we update the href with the elements inside the urlParts array
    this.updateHref();
  }

  /**
   * Updates the UrlParts for multiple namespaces when a `url-option-update` event occurs.
   *
   * It will search for the key provided by `event.detail.key` inside the elementOptions
   * and will add/update the urlParts for that `<sw-router-link-target>` element.
   *
   * Then the urlParts will update with every key found inside the elementOptions and
   * updateHref method is called to refresh the `<sw-router-link>` href attribute.
   *
   * @ignore
   * @param {CustomEvent} optionUpdateEvent The update event of the `<sw-router-link-target>`
   * @memberof RouterLink
   */
  private refreshUrlFromElementOptions(optionUpdateEvent: CustomEvent): void {
    optionUpdateEvent.stopPropagation();

    // Checks event detail and searches for key in elementOptions
    if (optionUpdateEvent.detail) {
      const { key, urlParts } = optionUpdateEvent.detail;
      if (this.hasValidNamespace(urlParts.namespace)) {
        this.elementOptions.set(key, urlParts);
      }
    }
    this.updateHref();
  }

  /**
   * Cascades useful anchor attributes to the router-link anchor. If attributes are removed,
   * they are also removed from the anchor.
   *
   * @ignore
   * @param {string} name name of attribute that was changed
   * @param {string | null} value new Value of the the attribute that was changed
   * @memberof RouterLink
   */
  private cascadeAttribute(name: string, value: string | null): void {
    if (value != null) {
      this.anchorElement.setAttribute(name, value);
      return;
    }
    this.anchorElement.removeAttribute(name);
  }

  /**
   *
    The Web Component API function that is called every time an attribute in the
   * `observedAttributes` array is modified from the outsides.
   *
   * It updates the internal representation of all attributes and recomputes the
   * resolved URL by using the routing library.
   *
   * @ignore
   * @param {string} name The name of the attribute that changed
   * @param {string} _oldValue The previous value of the attribute
   * @param {string} newValue The new value of the attribute
   * @memberof RouterLink
   */
  attributeChangedCallback(name: string, _oldValue: string | null, newValue: string | null): void {
    // TODO: add support for fallthrough of `target` and `rel` anchor attributes
    const internalOption = this.elementOptions.get(this.internalTargetKey);
    if (internalOption) {
      switch (name) {
        case 'path':
        case 'pathname':
          internalOption.path = newValue;
          break;
        case 'hash':
          internalOption.hash = newValue;
          break;
        case 'search-params': {
          if (newValue != null) {
            const paramEntries = newValue.split('&').map((param) => param.split('='));
            const paramObject = Object.fromEntries(paramEntries);
            internalOption.searchParams = paramObject;
            break;
          }
          internalOption.searchParams = newValue;
          break;
        }
        case 'base':
          this.elementGlobalOptions.base = newValue;
          break;
        case 'clear':
          this.elementGlobalOptions.clear = newValue !== null;
          break;
        case 'target':
          this.cascadeAttribute(name, newValue);
          break;
        case 'hreflang':
          this.cascadeAttribute(name, newValue);
          break;
        case 'referrerpolicy':
          this.cascadeAttribute(name, newValue);
          break;
        case 'prefetch':
          internalOption.prefetch = newValue !== null;
          break;
      }

      this.updateHref();
    }
  }

  /**
   *
    The Web Component API function that is called every time this element instance is added onto
   * the DOM tree.
   *
   * We use this hook to add the URLChangeEventListener, since it doesn't make sense to update
   * the link if it is not present in the DOM.
   *
   * @ignore
   * @memberof RouterLink
   */
  connectedCallback(): void {
    addUrlChangeListener(this.onUrlChange);
    this.updateHref();
  }

  /**
   *
    The Web Component API function that is called every time this element instance is added onto
   * the DOM tree.
   *
   * We use this hook to remove the URLChangeEventListener, since it doesn't make sense to update
   * the link if it is not present in the DOM.
   *
   * @ignore
   * @memberof RouterLink
   */
  disconnectedCallback(): void {
    removeUrlChangeListener(this.onUrlChange);
  }

  /**
   * The execution hook that is called after the click event is triggered but before navigation
   * occurs. If the given `handler` returns a `Promise`, it will be awaited on.
   *
   * @param {BeforeNavigationFunction | null} handler The handler function
   * @memberof RouterLink
   */
  set beforeNavigation(handler: BeforeNavigationFunction | null) {
    // Since this is a public API, that can be accessed via native JS
    // we need to do runtime typechecks to avoid breaking further down the line
    if (handler == null || typeof handler === 'function') {
      this.beforeHook = handler || null;
    } else {
      throw new Error('[sw-router-link] handler must be of type Function | null');
    }
  }
  /**
   * helper method that checks if there is a valid namespace it returns depending on the namespace validity.
   *
   * Furthermore, this helper can check namespace validity from the targets and from the router-link itself. If there is
   * a namespace argument, the method knows that it is checking namespace validity for router-link-target.
   *
   * 1- router-link -> if valid namespace, it populates the elementOptions array with the correct
   * namespace. Finally it returns depending on the namespace validity.
   * 2- router-link-target -> only checks for namespace validity and it does not updates the elementOptions array
   *
   * @ignore
   * @private
   * @param {targetNamespace} string router-link-target namespace
   * @return {*}  {boolean}
   */
  private hasValidNamespace(targetNamespace?: string | null): boolean {
    const namespace = targetNamespace ? targetNamespace : this.getAttribute('namespace');
    if (namespace == null || namespace.length === 0) {
      return false;
    }
    if (!targetNamespace) {
      const internalOption = this.elementOptions.get(this.internalTargetKey);
      if (internalOption) {
        internalOption.namespace = namespace;
      }
    }
    return true;
  }
  /**
   *
   * return true if the current link meets the criteria to call `navigateTo`
   *
   * if there is a base attribute or if the navigation is meant to be in a new page this method prevents the navigateTo call
   * and lets the browser handle the link normally (default navigation)
   *
   * @ignore
   * @private
   * @return {*}  {boolean}
   */
  private isAbleToCallNavigate(): boolean {
    if (this.anchorElement.getAttribute('target') === '_blank') {
      return false;
    }
    if (this.elementGlobalOptions.base) {
      return hasSharedBase(this.elementGlobalOptions.base);
    }
    return true;
  }

  /**
   * Calling this function will resolve a new URL from the current `urlParts` object
   * and set it in the `anchorElement`'s `href` attribute. If the URL resolution resolves
   * to `null`, the attribute is removed instead.
   *
   * @ignore
   * @private
   * @memberof RouterLink
   */
  private updateHref() {
    const urlParts = [];

    if (this.hasTargetChildren) {
      // Pick every option from target links
      for (const [key, value] of this.elementOptions.entries()) {
        if (key !== this.internalTargetKey) {
          urlParts.push(value);
        }
      }
    } else {
      // Pick option from `<sw-router-link>`
      const internalOptions = this.elementOptions.get(this.internalTargetKey);
      if (internalOptions) {
        urlParts.push(internalOptions);
      }
    }

    if (urlParts.length === 0) {
      this.anchorElement.removeAttribute('href');
      this.removePrefetchLink();
      return;
    }

    if (!this.hasValidNamespace() && !this.hasTargetChildren) {
      // checks if namespace is not valid only when there are no children
      this.anchorElement.removeAttribute('href');
      this.removePrefetchLink();
    } else {
      // TODO: add support for active class of some sor
      this.targetUrl = resolve(urlParts, this.elementGlobalOptions);
      if (this.targetUrl == null) {
        this.anchorElement.removeAttribute('href');
        this.removePrefetchLink();
      } else {
        this.anchorElement.setAttribute('href', this.targetUrl.href);

        // Check if prefetch attribute exists
        if (urlParts.filter((urlPart) => urlPart.prefetch).length > 0) {
          this.setOnHoverPrefetch();
        }
      }
    }
  }

  private removePrefetchLink(): void {
    if (this.linkElement) {
      document.head.removeChild(this.linkElement);
      this.linkElement = null;
    }
  }

  /**
   * Click handler for the `anchorElement`. It awaits on the `beforeNavigation` hook, if present,
   * and requests navigation to the current `targetUrl` via the routing library.
   *
   * Furthermore, if we do not have the same base or if the link is meant to be open on a different page
   * the method will let the browser handle the navigation as it would normally.
   *
   * @ignore
   * @private
   * @param {MouseEvent} clickEvent
   * @memberof RouterLink
   */
  private async onClickHandler(clickEvent: MouseEvent) {
    if (this.isAbleToCallNavigate()) {
      clickEvent.preventDefault();
      if (this.targetUrl != null) {
        if (this.beforeHook != null) {
          await this.beforeHook();
        }

        window.requestAnimationFrame(() => this.targetUrl && navigateTo(this.targetUrl));
      }
    }
  }
}

/**
 * Register the RouterLink in the browser's `customElements` registry with the given `tagName`.
 *
 * @export
 * @param {string} [tagName='sw-router-link'] The target tag name
 * @return {*}  {void}
 */
export function define(tagName = 'sw-router-link'): void {
  defineElement(RouterLink, tagName);
}
